import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import cx from 'classnames'
import { AIJobStatus, BrandProfileAnalysis } from 'types'
import { usePollBrandProfileAnalysis } from 'components/FirstTimeExperienceModal/steps/BrandProfile/usePollBrandProfileAnalysis'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useAsyncScreenshot } from 'hooks/queries/useAsyncScreenshot'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Button } from 'blaze-ui/Button'
import { TopBar } from 'screens/VirtualMarketerOnboarding/TopBar/TopBar'
import { V4LoaderArrow } from 'screens/VirtualMarketerOnboarding/V4LoaderArrow'
import { useLoaderAnimation, scheduleHeadlines } from 'screens/VirtualMarketerOnboarding/v4LoaderUtils'
import { Check } from 'blaze-ui/icons/16'
import Styles from './V4ProfileLoader.module.scss'

const HEADLINE_PHASES = [
  { at: 0, text: 'Learning about your business' },
  { at: 5000, text: 'Pulling photos & media' },
  { at: 11000, text: 'Identifying your brand' },
  { at: 20000, text: 'Understanding your business' },
  { at: 28000, text: 'Building your profile' },
]

const TW_SPEED = 36
const TW_JITTER = 7
const NAME_START_DELAY = 4000
const NAME_SPEED = 55
const NAME_JITTER = 10

const PLACEHOLDER_NAME = 'Acme Studios (Example)'
const PLACEHOLDER_ROWS = ['Service-based, Consultant', 'Confident, Warm, Expert', 'Trusted partner for growth']
const ROW_LABELS = ['Type', 'Audience', 'Position']
const ROW_START_TIMES = [10500, 16000, 21500]

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function getRowValue(index: number, a: BrandProfileAnalysis): string | null {
  switch (index) {
    case 0: {
      const parts: string[] = []
      if (a.business_type) parts.push(titleCase(a.business_type))
      if (a.business_industry) parts.push(titleCase(a.business_industry))
      return parts.length ? parts.join(', ') : null
    }
    case 1: {
      const parts: string[] = []
      if (a.audience_gender) parts.push(titleCase(a.audience_gender))
      if (a.audience_min_age != null && a.audience_max_age != null)
        parts.push(`${a.audience_min_age}–${a.audience_max_age}`)
      return parts.length ? parts.join(', ') : null
    }
    case 2:
      return a.primary_market_locations || null
    default:
      return null
  }
}

function analysisHasData(a: BrandProfileAnalysis): boolean {
  return !!(a.name || a.business_type || a.business_industry || a.primary_market_locations)
}

type RowState = { text: string; done: boolean; visible: boolean }
type RowAction =
  | { type: 'SET_TEXT'; index: number; text: string }
  | { type: 'SET_VISIBLE'; index: number }
  | { type: 'SET_DONE'; index: number }
  | { type: 'RESET'; index: number }

function rowReducer(state: RowState[], action: RowAction): RowState[] {
  const next = [...state]
  switch (action.type) {
    case 'SET_TEXT':
      next[action.index] = { ...next[action.index], text: action.text }
      return next
    case 'SET_VISIBLE':
      next[action.index] = { ...next[action.index], visible: true }
      return next
    case 'SET_DONE':
      next[action.index] = { ...next[action.index], done: true }
      return next
    case 'RESET':
      next[action.index] = { ...next[action.index], text: '', done: false }
      return next
    default:
      return state
  }
}

const INITIAL_ROWS: RowState[] = ROW_LABELS.map(() => ({ text: '', done: false, visible: false }))

const ProfileRow = ({ label, text, active, done }: { label: string; text: string; active: boolean; done: boolean }) => (
  <div
    className={cx(Styles.profileRow, {
      [Styles.profileRowShow]: active,
      [Styles.profileRowDone]: done,
      [Styles.profileRowWriting]: active && !done,
    })}
  >
    <span className={Styles.rowLabel}>{label}</span>
    <span className={Styles.rowValue}>
      {text}
      {active && !done && <span className={cx(Styles.cursor, Styles.cursorOn)} />}
    </span>
    <div className={cx(Styles.rowCheck, { [Styles.rowCheckShow]: done })}>
      <Check size={14} color="var(--green-50)" />
    </div>
  </div>
)

interface Props {
  analysisId: number
  brandProfileId: number
  brandKitId?: number
  websiteUrl?: string
  onReady: () => void
  onBack: (withError?: boolean) => void
}

export const V4ProfileLoader = ({ analysisId, brandProfileId, brandKitId, websiteUrl, onReady, onBack }: Props) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const hasAdvancedRef = useRef(false)
  const hasStartedScreenshotRef = useRef(false)

  const { data: brandKitData } = useBrandKit(brandKitId, {
    pollUntilFinal: true,
    placeholderData: undefined,
    staleTime: 0,
  })
  const brandKit = brandKitData?.brand_kit
  const logoSrc = brandKit?.logo_url ?? brandKit?.websites?.[0]?.favicon ?? null

  const { screenshotUrl, createScreenshot } = useAsyncScreenshot()
  useEffect(() => {
    if (hasStartedScreenshotRef.current || !websiteUrl) return
    hasStartedScreenshotRef.current = true
    createScreenshot(websiteUrl).catch(() => {})
  }, [websiteUrl, createScreenshot])
  const displayScreenshot = screenshotUrl ?? brandKit?.websites?.[0]?.screenshot_url ?? null

  const { addTimer, clearTimers, typewrite, activeHeadline, setActiveHeadline, exitHeadline, swapHeadline } =
    useLoaderAnimation(TW_SPEED, TW_JITTER)

  const typewriteName = useCallback(
    (text: string, setter: (s: string) => void, onDone: () => void) => {
      let i = 0
      const tick = () => {
        if (i < text.length) {
          i++
          setter(text.slice(0, i))
          addTimer(tick, NAME_SPEED + (Math.random() * NAME_JITTER * 2 - NAME_JITTER))
        } else {
          addTimer(onDone, 220)
        }
      }
      tick()
    },
    [addTimer],
  )

  const [nameText, setNameText] = useState('')
  const [nameDone, setNameDone] = useState(false)
  const [nameActive, setNameActive] = useState(false)
  const nameUsedPlaceholderRef = useRef(true)

  const [rows, dispatchRow] = useReducer(rowReducer, INITIAL_ROWS)
  const allRowsDone = rows.every((r) => r.done)
  const rowUsedPlaceholderRef = useRef<boolean[]>(ROW_LABELS.map(() => true))

  const [analysisDone, setAnalysisDone] = useState(false)
  const analysisRef = useRef<BrandProfileAnalysis | null>(null)
  const hasRetypedRef = useRef(false)

  const { data: analysisData } = usePollBrandProfileAnalysis(
    { id: analysisId, brandProfileId },
    {
      onSuccess: async (data) => {
        if (hasAdvancedRef.current) return
        const { status } = data.brand_profile_analysis
        if (status === AIJobStatus.Successful) {
          await queryClient.refetchQueries(['workspaces', workspaceId, 'brandProfiles', 'detail', brandProfileId])
          setAnalysisDone(true)
        } else if (status === AIJobStatus.Failed || status === AIJobStatus.Expired) {
          hasAdvancedRef.current = true
          showErrorMessageToast("We could not analyze your website, please verify that it's correct and try again.")
          onBack(true)
        }
      },
    },
  )

  analysisRef.current = analysisData?.brand_profile_analysis ?? null

  const retypeRow = useCallback(
    (index: number, text: string) => {
      dispatchRow({ type: 'RESET', index })
      addTimer(() => {
        typewrite(
          text,
          (v) => dispatchRow({ type: 'SET_TEXT', index, text: v }),
          () => dispatchRow({ type: 'SET_DONE', index }),
        )
      }, 150)
    },
    [addTimer, typewrite],
  )

  useEffect(() => {
    const a = analysisData?.brand_profile_analysis
    if (!a || !analysisHasData(a) || hasRetypedRef.current) return
    hasRetypedRef.current = true

    if (a.name && nameUsedPlaceholderRef.current && nameDone) {
      nameUsedPlaceholderRef.current = false
      setNameDone(false)
      setNameActive(true)
      setNameText('')
      typewriteName(a.name, setNameText, () => {
        setNameDone(true)
        setNameActive(false)
      })
    }

    ROW_LABELS.forEach((_, i) => {
      const real = getRowValue(i, a)
      if (!real || !rowUsedPlaceholderRef.current[i]) return
      if (!rows[i].done) return
      rowUsedPlaceholderRef.current[i] = false
      retypeRow(i, real)
    })
  }, [analysisData, nameDone, rows, typewriteName, retypeRow]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowDone = useCallback(
    (index: number) => {
      dispatchRow({ type: 'SET_DONE', index })

      if (!hasRetypedRef.current || !rowUsedPlaceholderRef.current[index]) return
      const a = analysisRef.current
      if (!a || !analysisHasData(a)) return
      const real = getRowValue(index, a)
      if (!real) return
      rowUsedPlaceholderRef.current[index] = false
      retypeRow(index, real)
    },
    [retypeRow],
  )

  useEffect(() => {
    scheduleHeadlines(HEADLINE_PHASES, setActiveHeadline, swapHeadline, addTimer)

    addTimer(() => {
      setNameActive(true)
      const a = analysisRef.current
      const realName = a?.name || null
      const nameToType = realName ?? PLACEHOLDER_NAME
      if (realName) nameUsedPlaceholderRef.current = false
      typewriteName(nameToType, setNameText, () => {
        setNameDone(true)
        setNameActive(false)
      })
    }, NAME_START_DELAY)

    ROW_START_TIMES.forEach((startAt, i) => {
      addTimer(() => {
        dispatchRow({ type: 'SET_VISIBLE', index: i })

        addTimer(() => {
          const a = analysisRef.current
          const real = a ? getRowValue(i, a) : null
          const text = real ?? PLACEHOLDER_ROWS[i]
          if (real) rowUsedPlaceholderRef.current[i] = false

          typewrite(
            text,
            (v) => dispatchRow({ type: 'SET_TEXT', index: i, text: v }),
            () => handleRowDone(i),
          )
        }, 250)
      }, startAt)
    })

    return clearTimers
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasAdvancedRef.current) return
    if (analysisDone && allRowsDone) {
      hasAdvancedRef.current = true
      addTimer(() => onReady(), 600)
    }
  }, [analysisDone, allRowsDone, onReady, addTimer])

  const domain = websiteUrl ? websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'yourbusiness.com'

  return (
    <div className={Styles.page}>
      <TopBar highlightStep="gettingStarted" />
      <div className={Styles.wrap}>
        <div className={Styles.copyBlock}>
          <div className={Styles.statusRow}>
            <span>In progress</span>
            <span className={Styles.dot}>.</span>
            <span className={Styles.dot}>.</span>
            <span className={Styles.dot}>.</span>
          </div>

          <div className={Styles.headlineSlot}>
            {exitHeadline && (
              <div className={cx(Styles.headline, Styles.headlineExit)} key={`exit-${exitHeadline}`}>
                {exitHeadline}
              </div>
            )}
            <div className={cx(Styles.headline, Styles.headlineEnter)} key={`active-${activeHeadline}`}>
              {activeHeadline}
            </div>
          </div>

          <p className={Styles.subText}>We read your site and build your brand profile, like this:</p>
        </div>

        <hr className={Styles.divider} />

        <div className={Styles.illusRow}>
          <div className={Styles.websiteCol}>
            <p className={Styles.illusLabel}>Your website:</p>
            <div className={Styles.mockup}>
              <div className={Styles.mockupBar}>
                <div className={Styles.mockupDots}>
                  <span className={Styles.mockupDot} />
                  <span className={Styles.mockupDot} />
                  <span className={Styles.mockupDot} />
                </div>
                <div className={Styles.mockupUrl}>{domain}</div>
              </div>
              <div className={cx(Styles.mockupBody, { [Styles.mockupBodyScreenshot]: !!displayScreenshot })}>
                {displayScreenshot ? (
                  <img src={displayScreenshot} alt="Website screenshot" className={Styles.screenshotImg} />
                ) : (
                  <>
                    <div className={Styles.skHero} />
                    <div className={Styles.skLines}>
                      <span className={Styles.skLine} style={{ width: '100%' }} />
                      <span className={Styles.skLine} style={{ width: '88%' }} />
                      <span className={Styles.skLine} style={{ width: '94%' }} />
                      <span className={Styles.skBtn} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={Styles.arrowCol}>
            <V4LoaderArrow />
          </div>

          <div className={Styles.cardCol}>
            <p className={Styles.illusLabel}>Brand profile:</p>
            <div className={Styles.profileCard}>
              <div className={Styles.profileHeader}>
                {logoSrc ? (
                  <img src={logoSrc} alt="" className={Styles.profileLogoImg} />
                ) : (
                  <div className={Styles.profileLogo} />
                )}
                <span className={Styles.profileName}>
                  {nameText}
                  {nameActive && !nameDone && <span className={cx(Styles.cursor, Styles.cursorOn)} />}
                </span>
              </div>
              <div className={Styles.profileDivider} />
              <div className={Styles.profileRows}>
                {ROW_LABELS.map((label, i) => (
                  <ProfileRow
                    key={label}
                    label={label}
                    text={rows[i].text}
                    active={rows[i].visible}
                    done={rows[i].done}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={Styles.footer}>
        <div className={Styles.cancelBtn}>
          <Button variant="tertiary" size="lg" onPress={() => onBack()}>
            Back
          </Button>
        </div>
      </div>
    </div>
  )
}
